/**
 * useDockActions - Context for injecting page-specific action buttons into UniversalDock
 *
 * WHY: Components deep in the tree (e.g., InlineReport inside QA) need to add
 * contextual actions (Submit for Review, Export PDF) to the bottom dock. This
 * context lets any child component register/unregister actions without prop drilling.
 *
 * USAGE (in a child component):
 *   const { setActions, clearActions } = useDockActions()
 *
 *   useEffect(() => {
 *     setActions([
 *       { id: 'export', label: 'Export PDF', icon: <Download className="w-3.5 h-3.5" />, onClick: handleExport },
 *     ])
 *     return () => clearActions()
 *   }, [deps])
 *
 * @path packages/ui/src/hooks/useDockActions.tsx
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import type { DockAction } from '../components/UniversalDock'

interface DockActionsContextValue {
  /** Currently registered actions */
  actions: DockAction[]
  /** Set/replace all actions for the current page */
  setActions: (actions: DockAction[]) => void
  /** Clear all registered actions */
  clearActions: () => void
  /** Add a single action (idempotent by id) */
  addAction: (action: DockAction) => void
  /** Remove a single action by id */
  removeAction: (id: string) => void
}

const DockActionsContext = createContext<DockActionsContextValue>({
  actions: [],
  setActions: () => {},
  clearActions: () => {},
  addAction: () => {},
  removeAction: () => {},
})

export function DockActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActionsState] = useState<DockAction[]>([])

  const setActions = useCallback((newActions: DockAction[]) => {
    setActionsState(newActions)
  }, [])

  const clearActions = useCallback(() => {
    setActionsState([])
  }, [])

  const addAction = useCallback((action: DockAction) => {
    setActionsState((prev) => {
      const exists = prev.some((a) => a.id === action.id)
      if (exists) return prev.map((a) => (a.id === action.id ? action : a))
      return [...prev, action]
    })
  }, [])

  const removeAction = useCallback((id: string) => {
    setActionsState((prev) => prev.filter((a) => a.id !== id))
  }, [])

  return (
    <DockActionsContext.Provider value={{ actions, setActions, clearActions, addAction, removeAction }}>
      {children}
    </DockActionsContext.Provider>
  )
}

export function useDockActions() {
  return useContext(DockActionsContext)
}

export { DockActionsContext }
